import "./modal.scss";

import { Map } from "immutable";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";

import { MODAL_MASTER_LIST_VIEW } from "../../constants/identifiers";
import Dimensions from "../../constants/styles/dimensions";
import { PropTypeShapes } from "../../types";
import preprocessorQuery from "../../utils/preprocessorQuery";
import { withContainerContext } from "../container/ContainerContext";
import { asDropTarget } from "../dragAndDrop/DroppableComponent";
import ListView from "../listView/ListView";
import { canListItemDragByLocks } from "./utils";

const isIos = preprocessorQuery.isIos();

/**
 * Use drag handle only in iOS mode and there is any item can drag
 * @param {object[]} items
 * @param {bool} lockTopItemForDrag
 * @param {bool} lockBottomItemForDrag
 */
const getUseDragHandle = (items, lockTopItemForDrag, lockBottomItemForDrag, allowDrag) => {
  const itemsCount = items.length;
  let useDragHandle = isIos
    ? allowDrag &&
      items.some((i, index) => canListItemDragByLocks(index, itemsCount, lockTopItemForDrag, lockBottomItemForDrag))
    : false;

  return useDragHandle;
};

const canDropOnComponent = (props, dragItem) => {
  return props.allowDrag && dragItem.sourceView === props.viewId && dragItem.sourceViewType == MODAL_MASTER_LIST_VIEW;
};

const dropCallbacks = {
  onEnter: (_props, _dragItem, _dropTarget) => {
    // Do nothing
  },

  onLeave: (props, dragItem, dropTarget) => {
    return dropTarget.component.onLeave(props, dragItem, dropTarget);
  },

  getDragMultipleItemIds(props, dragItem, _dropTarget) {
    return [dragItem.itemId];
  },

  getDragStartParams(_props, _dragItem, _positions, dropTarget) {
    return dropTarget.component.getDragStartParams();
  },

  onHover: (props, dragItem, positions, dropTarget) => {
    return canDropOnComponent(props, dragItem)
      ? dropTarget.component.onHover(props, dragItem, positions, dropTarget)
      : null;
  },

  onDrop: (props, dragItem, positions, dropTarget) => {
    return canDropOnComponent(props, dragItem)
      ? dropTarget.component.onDrop(props, dragItem, positions, dropTarget)
      : null;
  },

  getDropPreviewForItem: (_props, _dragItem, _dropTarget) => {
    return null;
  }
};

const DEFAULT_AUTO_SCROLL_STEP = 5;
const DEFAULT_AUTO_SCROLL_INTERVAL = 50;

class ModalMasterList extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      dragIndex: null,
      dropIndex: null
    };
    this.itemRenderer = this.itemRenderer.bind(this);
    this.listItemRenderer = this.listItemRenderer.bind(this);
    this._domNode = null;
    this._scrollingTimer = null;
    this._scrollStep = 0;
    this._scrollTopWhenDragStart = -1;

    this.refMap = Map();
  }

  componentDidMount() {
    this.props.registerDropTarget(this, dropCallbacks);
  }

  componentDidUpdate(prevProps) {
    if (this.props.items.length === prevProps.items.length + 1) {
      const prevItems = prevProps.items;
      const thisItems = this.props.items;
      const newItemKey = thisItems.find(item => prevItems.every(pi => pi.value !== item.value)).value;
      const key = newItemKey ? newItemKey.toString() : newItemKey;
      const newItemRef = this.refMap.get(key).current;
      if (newItemRef) {
        newItemRef.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      }
    }
  }

  clearInterval() {
    if (this._scrollingTimer) {
      window.clearInterval(this._scrollingTimer);
      this._scrollingTimer = null;
      this._scrollStep = 0;
    }
  }

  buildInterval(isDown) {
    if ((isDown && this._scrollStep <= 0) || (!isDown && this._scrollStep >= 0)) {
      this.clearInterval();
      this._scrollStep = (isDown ? 1 : -1) * DEFAULT_AUTO_SCROLL_STEP;
      this._scrollingTimer = window.setInterval(() => {
        this._domNode.scroll({
          top: this._domNode.scrollTop + this._scrollStep,
          behavior: "smooth"
        });
      }, DEFAULT_AUTO_SCROLL_INTERVAL);
    }
  }

  getDragStartParams() {
    this._scrollTopWhenDragStart = this._domNode.scrollTop;
  }

  onHover(_props, dragItem, positions, _dropTarget) {
    // When reaching the edge
    // build the interval to perform auto scrolling
    if (this._domNode.getBoundingClientRect().bottom - positions.sourceRect.bottom < 20) {
      this.buildInterval(true);
    } else if (positions.sourceRect.top - this._domNode.getBoundingClientRect().top < 20) {
      this.buildInterval(false);
    } else {
      this.clearInterval();
    }

    // Since we truncate text rather than wrap, we can safely assume that all items are the same
    // size.
    const itemHeight = positions.sourceRect.height;

    // Work out where it is currently positioned
    const currentIndex = _.isNil(this.state.dropIndex) ? dragItem.index : this.state.dropIndex;

    const itemsCount = this.props.items.length;

    // In a single dragging action (mouse not released),
    // use the mouse offset and the scrolling offset to get the correct moving offset.
    // Then find the dropIndex by the relative distance to the dragging item and its index.
    let dropIndex =
      Math.ceil(
        (positions.screenPointerOffset.y + (this._domNode.scrollTop - this._scrollTopWhenDragStart)) / itemHeight
      ) + dragItem.index;

    // Clamp dropIndex within range
    if (dropIndex < 0) {
      dropIndex = 0;
    } else if (dropIndex > itemsCount) {
      dropIndex = itemsCount;
    }
    if (currentIndex < dropIndex) {
      dropIndex -= 1;
    }
    // Adjust if the top or bottom item is blocked from being dragged
    if (this.props.lockTopItemForDrag && dropIndex === 0) {
      dropIndex = 1;
    }
    if (this.props.lockBottomItemForDrag && dropIndex >= itemsCount - 1) {
      dropIndex = itemsCount - 2;
    }

    // And record that position in our state so we can animate the update
    this.setState({
      dragIndex: dragItem.index,
      dropIndex
    });
  }

  onLeave(_props, _dragItem, _dropTarget) {
    this.clearInterval();
    this.setState({
      dragIndex: null,
      dropIndex: null
    });
  }

  onDrop(_props, dragItem, _positions, _dropTarget) {
    if (!_.isNil(this.state.dropIndex) && this.props.allowDrag && this.props.onMoveItemAtIndex) {
      this.props.onMoveItemAtIndex(dragItem.index, this.state.dropIndex);
    }
    this.clearInterval();
    this._scrollTopWhenDragStart = -1;
    // Now move the dragged item to the correct index...
    this.setState({
      dragIndex: null,
      dropIndex: null
    });
  }

  itemRenderer(item, index, useDragHandle, width) {
    const { allowDrag, lockTopItemForDrag, lockBottomItemForDrag } = this.props;

    let slideUp = false;
    let slideDown = false;
    if (!_.isNil(this.state.dragIndex) && !_.isNil(this.state.dropIndex)) {
      // Dragging down
      if (this.state.dragIndex < this.state.dropIndex) {
        slideUp = index > this.state.dragIndex && index <= this.state.dropIndex;
      }
      if (this.state.dragIndex > this.state.dropIndex) {
        slideDown = index < this.state.dragIndex && index >= this.state.dropIndex;
      }
    }

    // Is this item allowed to be dragged at all?
    const numItems = this.props.items.length;
    const itemInBlockedList = !canListItemDragByLocks(index, numItems, lockTopItemForDrag, lockBottomItemForDrag);
    const canDrag = allowDrag && !itemInBlockedList;

    const renderedItem = this.props.itemRenderer(item, index, useDragHandle, width, slideUp, slideDown, canDrag);

    return renderedItem;
  }

  listItemRenderer(item, isSelected, ref) {
    // Store the ref for drag and drop
    // Watch out here key is string
    this.refMap = this.refMap.set(item.key, ref);
    return (
      <div key={item.key} ref={ref}>
        {item}
      </div>
    );
  }

  getListView() {
    const { items, selectedItemIndex, onItemClick } = this.props;

    const useDragHandle = getUseDragHandle(
      items,
      this.props.lockTopItemForDrag,
      this.props.lockBottomItemForDrag,
      this.props.allowDrag
    );

    let width = Dimensions.MODAL_MASTER_WIDTH;

    if (isIos) {
      width = null;
      // const leftPadding = this.props.paddingLeft
      //   ? Math.max(this.props.paddingLeft, Dimensions.MODAL_X_PADDING)
      //   : Dimensions.MODAL_X_PADDING;
      // const rightPadding = this.props.paddingRight ? Math.max(this.props.paddingRight, 0) : 0;
      // width = Math.max(this.props.containerRect.width, Dimensions.MODAL_MASTER_WIDTH) - leftPadding - rightPadding;
      // if (isMobile) {
      // width = this.props.containerRect.width - leftPadding - rightPadding;
      //   width = null;
      // }
    }

    const renderedItems = items.map((i, index) => this.itemRenderer(i, index, useDragHandle, width));

    return (
      <ListView
        disabled={this.props.disabled}
        items={renderedItems}
        selectedItemIndex={selectedItemIndex}
        onItemSelect={onItemClick}
        itemRenderer={this.listItemRenderer}
        isEnterToSelect={false}
      />
    );
  }

  render() {
    const dragInProgress = !_.isNil(this.state.dragIndex);
    return (
      <div className={`modalMasterList ${dragInProgress ? "dragInProgress" : ""}`} ref={node => (this._domNode = node)}>
        {this.getListView()}
      </div>
    );
  }
}

ModalMasterList.propTypes = {
  disabled: PropTypes.bool,
  items: PropTypes.oneOfType([PropTypes.array]).isRequired,
  itemRenderer: PropTypes.func.isRequired,
  viewId: PropTypes.string.isRequired,

  allowDrag: PropTypes.bool,
  lockTopItemForDrag: PropTypes.bool,
  lockBottomItemForDrag: PropTypes.bool,
  onMoveItemAtIndex: PropTypes.func,

  // For list navigation
  selectedItemIndex: PropTypes.number,
  onItemClick: PropTypes.func,

  // From asDropTarget
  registerDropTarget: PropTypes.func.isRequired,

  //#!if iOS === true
  // From containerContext
// containerRect: PropTypeShapes.flatRect.isRequired,
// paddingLeft: PropTypes.number,
// paddingRight: PropTypes.number
  //#!endif
};

let FinalComponent = ModalMasterList;

//#!if iOS === true
// FinalComponent = withContainerContext(ModalMasterList);
//#!endif

export default asDropTarget(FinalComponent, MODAL_MASTER_LIST_VIEW);
